import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ActiveTest } from '../utils/local-api';
import { UserExamRestrictions } from '../utils/api';
import {
  BookOpen,
  Shuffle,
  FileText,
  PenTool,
  PlayCircle,
  Lock,
  ChevronRight,
} from 'lucide-react';
import HSK30Systems, { CSCASystems } from '../components/HSK30Systems';

interface DashboardMobileProps {
  activeTests: ActiveTest[];
  historyCount: Record<string, number>;
  restrictions: UserExamRestrictions;
}

const LEVELS = [1, 2, 3, 4, 5, 6];
const AVAILABLE_HSK20_LEVELS = new Set([2, 3, 4]);

type ExamVersion = '2.0' | '3.0' | 'CSCA';

const DashboardMobile: React.FC<DashboardMobileProps> = ({ activeTests, historyCount, restrictions }) => {
  const availableVersions: Array<{ id: ExamVersion; label: string }> = [
    ...(!restrictions.hsk2Restricted && restrictions.hsk2Levels.length > 0 ? [{ id: '2.0' as const, label: 'HSK 2.0' }] : []),
    ...(!restrictions.hsk3Restricted && restrictions.hsk3Levels.length > 0 ? [{ id: '3.0' as const, label: 'HSK 3.0' }] : []),
    ...(!restrictions.cscaRestricted && restrictions.cscaLevels.length > 0 ? [{ id: 'CSCA' as const, label: 'CSCA' }] : []),
  ];
  const [selectedVersion, setSelectedVersion] = useState<ExamVersion>(availableVersions[0]?.id || '2.0');
  const [selectedLevel, setSelectedLevel] = useState<number | null>(() => {
    const first = availableVersions[0]?.id;
    return first === '3.0' ? restrictions.hsk3Levels[0] || null : first === 'CSCA' ? restrictions.cscaLevels[0] || null : restrictions.hsk2Levels[0] || null;
  });

  useEffect(() => {
    if (availableVersions.some(version => version.id === selectedVersion)) return;
    const first = availableVersions[0]?.id;
    if (!first) return;
    setSelectedVersion(first);
    setSelectedLevel(first === '3.0' ? restrictions.hsk3Levels[0] || null : first === 'CSCA' ? restrictions.cscaLevels[0] || null : restrictions.hsk2Levels[0] || null);
  }, [availableVersions, restrictions, selectedVersion]);

  const isLevelAvailable = (lvl: number) => restrictions.hsk2Levels.includes(lvl) && AVAILABLE_HSK20_LEVELS.has(lvl);

  const renderActiveTests = () => {
    if (activeTests.length === 0) return null;
    return (
      <section>
        <h2 className="text-base font-bold text-[#312E81] mb-3 px-1">Continue Learning</h2>
        <div className="space-y-3">
          {activeTests.map(test => {
            const key = `${test.bank_version || '2.0'}-${test.system_type || 'simulation'}-${test.level}-${test.part}-${test.workbook_part || ''}-${test.paper_id || ''}-${test.topic_coarse || ''}-${test.topic_fine || ''}`;
            const num = (historyCount[key] || 0) + 1;
            const percent = Math.round(((test.current_index + 1) / test.total) * 100);
            const resumePath = test.system_type === 'random_paper'
              ? '/csca/random/exam'
              : test.system_type === 'mooc_random'
              ? '/csca/mooc/random/exam'
              : test.system_type === 'mooc_topic_practice' && test.topic_coarse
              ? `/csca/mooc/topic-practice?${new URLSearchParams({ coarse: test.topic_coarse, ...(test.topic_fine ? { fine: test.topic_fine } : {}), count: String(test.total) }).toString()}`
              : test.system_type === 'topic_practice' && test.topic_coarse
              ? `/csca/topic-practice?${new URLSearchParams({ coarse: test.topic_coarse, ...(test.topic_fine ? { fine: test.topic_fine } : {}), count: String(test.total) }).toString()}`
              : test.system_type === 'past_paper' && test.paper_id
              ? `/csca/past-papers/${test.paper_id}`
              : test.system_type === 'workbook'
              ? `/hsk3/workbook/hsk${test.level}`
              : test.bank_version === '3.0'
                ? `/hsk3/simulation/hsk${test.level}`
                : test.part === 'simulation' ? `/simulation/hsk${test.level}` : `/test/hsk${test.level}/${test.part}`;

            return (
              <Link
                key={test.id}
                to={test.part === 'simulation' && (test.bank_version || '2.0') === '2.0' ? `${resumePath}?testId=${encodeURIComponent(test.id)}` : resumePath}
                className="block bg-white rounded-2xl p-4 border border-indigo-50 active:bg-indigo-50/40"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
                    <PlayCircle className="w-6 h-6 text-[#4F46E5]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#312E81] truncate">
                      {test.system_type === 'mooc_random' ? 'CSCA 数学 · 随机 MOOC' : test.system_type === 'mooc_topic_practice' ? `CSCA 数学 · MOOC · ${test.topic_fine ? `${test.topic_coarse} / ${test.topic_fine}` : test.topic_coarse} · 分类练习` : test.system_type === 'random_paper' ? 'CSCA 数学 · 随机组卷' : test.system_type === 'topic_practice' ? `CSCA 数学 · ${test.topic_fine ? `${test.topic_coarse} / ${test.topic_fine}` : test.topic_coarse} · 题型练习` : test.system_type === 'past_paper' ? `CSCA 数学 ${test.paper_year || ''}年${test.paper_month || ''}月` : `HSK ${test.level} ${test.system_type === 'workbook' ? `Workbook${test.workbook_part ? ` · ${test.workbook_part}` : ''}` : test.part.charAt(0).toUpperCase() + test.part.slice(1)}`} ({num})
                    </div>
                    <div className="text-xs text-gray-400">{percent}% complete</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 shrink-0" />
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="bg-[#4F46E5] h-full rounded-full transition-all" style={{ width: `${percent}%` }} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    );
  };

  const PracticeCard: React.FC<{
    to: string;
    title: string;
    subtitle: string;
    badge: string;
    Icon: React.ComponentType<{ className?: string }>;
    accent: 'indigo' | 'emerald' | 'purple' | 'cyan';
  }> = ({ to, title, subtitle, badge, Icon, accent }) => {
    const styles = {
      indigo: { bg: 'bg-indigo-100', text: 'text-[#4F46E5]', badgeBg: 'bg-indigo-50', badgeText: 'text-[#4F46E5]' },
      emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-600' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', badgeBg: 'bg-purple-50', badgeText: 'text-purple-600' },
      cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', badgeBg: 'bg-cyan-50', badgeText: 'text-cyan-600' },
    }[accent];

    return (
      <Link
        to={to}
        className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-gray-100 active:bg-gray-50"
      >
        <div className={`w-12 h-12 ${styles.bg} rounded-xl flex items-center justify-center ${styles.text} shrink-0`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-bold text-[#312E81]">{title}</span>
            <span className={`${styles.badgeBg} ${styles.badgeText} px-2 py-0.5 rounded-full text-[10px] font-bold`}>
              {badge}
            </span>
          </div>
          <div className="text-xs text-gray-500">{subtitle}</div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 shrink-0" />
      </Link>
    );
  };

  const renderLevelContent = () => {
    if (availableVersions.length === 0) {
      return <div className="rounded-2xl border border-gray-100 bg-white p-6 text-center text-sm text-gray-500">当前账户没有已开放的考试体系。</div>;
    }
    if (selectedVersion === '3.0') {
      return <HSK30Systems allowedLevels={restrictions.hsk3Levels} />;
    }
    if (selectedVersion === 'CSCA') {
      return <CSCASystems allowedLevels={restrictions.cscaLevels} />;
    }

    if (selectedLevel === 3) {
      return (
        <div className="space-y-3">
          {/* Featured: Full Simulation */}
          <Link
            to="/simulation/hsk3"
            className="block bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg shadow-orange-500/20"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-white backdrop-blur-sm">
                <Shuffle className="w-6 h-6" />
              </div>
              <div className="bg-white/20 text-white px-2.5 py-0.5 rounded-full text-[10px] font-bold backdrop-blur-sm">
                Full Exam
              </div>
            </div>
            <h3 className="text-lg font-bold mb-0.5">Full Simulation</h3>
            <p className="text-orange-100 text-xs mb-3">Listening + Reading + Writing</p>
            <div className="flex items-center text-white font-bold text-sm">
              Start Exam <ChevronRight className="w-4 h-4 ml-1" />
            </div>
          </Link>

          <PracticeCard to="/test/hsk3/listening" title="Listening" subtitle="40 题 · 35 分钟" badge="Part 1" Icon={BookOpen} accent="indigo" />
          <PracticeCard to="/test/hsk3/reading" title="Reading" subtitle="30 题 · 30 分钟" badge="Part 2" Icon={FileText} accent="emerald" />
          <PracticeCard to="/test/hsk3/writing" title="Writing" subtitle="10 题 · 15 分钟" badge="Part 3" Icon={PenTool} accent="purple" />

          {renderActiveTests()}
        </div>
      );
    }

    if (selectedLevel === 4) {
      return (
        <div className="space-y-3">
          <Link
            to="/simulation/hsk4"
            className="block bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg shadow-orange-500/20"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-white backdrop-blur-sm">
                <Shuffle className="w-6 h-6" />
              </div>
              <div className="bg-white/20 text-white px-2.5 py-0.5 rounded-full text-[10px] font-bold backdrop-blur-sm">
                HSK 4
              </div>
            </div>
            <h3 className="text-lg font-bold mb-0.5">Full Simulation</h3>
            <p className="text-orange-100 text-xs mb-3">100 题 · 听力 + 阅读 + 写作</p>
            <div className="flex items-center text-white font-bold text-sm">
              Start Exam <ChevronRight className="w-4 h-4 ml-1" />
            </div>
          </Link>

          <PracticeCard to="/test/hsk4/listening" title="Listening" subtitle="45 题 · 可自定义" badge="Part 1" Icon={BookOpen} accent="indigo" />
          <PracticeCard to="/test/hsk4/reading" title="Reading" subtitle="40 题 · 可自定义" badge="Part 2" Icon={FileText} accent="emerald" />
          <PracticeCard to="/test/hsk4/writing" title="Writing" subtitle="15 题 · 可自定义" badge="Part 3" Icon={PenTool} accent="purple" />

          {renderActiveTests()}
        </div>
      );
    }

    if (selectedLevel === 2) {
      return (
        <div className="space-y-3">
          <Link
            to="/simulation/hsk2"
            className="block bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg shadow-orange-500/20"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-white backdrop-blur-sm">
                <Shuffle className="w-6 h-6" />
              </div>
              <div className="bg-white/20 text-white px-2.5 py-0.5 rounded-full text-[10px] font-bold backdrop-blur-sm">
                HSK 2
              </div>
            </div>
            <h3 className="text-lg font-bold mb-0.5">Full Simulation</h3>
            <p className="text-orange-100 text-xs mb-3">60 题 · 听力 + 阅读</p>
            <div className="flex items-center text-white font-bold text-sm">
              Start Exam <ChevronRight className="w-4 h-4 ml-1" />
            </div>
          </Link>

          <PracticeCard to="/test/hsk2/listening" title="Listening" subtitle="35 题 · 可自定义" badge="Part 1" Icon={BookOpen} accent="indigo" />
          <PracticeCard to="/test/hsk2/reading" title="Reading" subtitle="25 题 · 可自定义" badge="Part 2" Icon={FileText} accent="emerald" />

          {renderActiveTests()}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl p-6 text-center border border-gray-100">
        <div className="w-14 h-14 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-3">
          <Lock className="w-7 h-7" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">Level Locked</h3>
        <p className="text-sm text-gray-500">HSK {selectedVersion} Level {selectedLevel} 暂未开放</p>
      </div>
    );
  };

  return (
    <div className="space-y-5 pb-4">
      <div>
        <h1 className="text-2xl font-bold text-[#312E81] px-1">Dashboard</h1>
        <p className="text-sm text-gray-500 px-1 mt-0.5">选择版本和等级开始练习</p>
      </div>

      {/* Version Toggle */}
      <div className="bg-white rounded-2xl p-1 flex border border-gray-100">
        {availableVersions.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => {
              setSelectedVersion(id);
              if (id === '3.0') setSelectedLevel(restrictions.hsk3Levels[0] || null);
              if (id === '2.0') setSelectedLevel(restrictions.hsk2Levels[0] || null);
              if (id === 'CSCA') setSelectedLevel(restrictions.cscaLevels[0] || null);
            }}
            className={`flex-1 py-2.5 rounded-xl font-bold text-base transition-all ${
              selectedVersion === id
                ? 'bg-[#4F46E5] text-white shadow-md shadow-indigo-500/20'
                : 'text-gray-400 active:text-[#312E81]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Horizontal Level Picker */}
      {selectedVersion === '2.0' && <div className="-mx-4 px-4 overflow-x-auto scrollbar-none">
        <div className="flex gap-2.5 pb-1">
          {LEVELS.filter(lvl => restrictions.hsk2Levels.includes(lvl)).map((lvl) => {
            const available = isLevelAvailable(lvl);
            const active = selectedLevel === lvl;
            return (
              <button
                key={lvl}
                onClick={() => available && setSelectedLevel(lvl)}
                disabled={!available}
                className={`relative shrink-0 w-16 h-20 rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                  active
                    ? 'border-[#4F46E5] bg-indigo-50'
                    : 'border-gray-100 bg-white'
                } ${!available ? 'opacity-50' : ''}`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-lg ${
                  active ? 'bg-[#4F46E5] text-white' : 'bg-gray-100 text-gray-400'
                }`}>
                  {lvl}
                </div>
                <span className={`text-[10px] font-bold ${active ? 'text-[#312E81]' : 'text-gray-400'}`}>
                  HSK {lvl}
                </span>
                {!available && (
                  <Lock className="absolute top-1.5 right-1.5 w-3 h-3 text-gray-300" />
                )}
              </button>
            );
          })}
        </div>
      </div>}

      {/* Level Content */}
      {renderLevelContent()}
    </div>
  );
};

export default DashboardMobile;
